import type { TFunction } from 'i18next';
import type { AxiosError } from 'axios';

interface ApiErrorResponse {
  error?: string;
  message?: string;
}

/**
 * Translates a backend API error into a localized message.
 *
 * Reads the `error` code from the response body and looks up
 * `apiError.<CODE>` in the current locale. Falls back to the
 * raw `message` field, then to the provided `fallback`.
 */
export function translateApiError(
  err: unknown,
  t: TFunction,
  fallback?: string
): string {
  const axiosErr = err as AxiosError<ApiErrorResponse>;
  const data = axiosErr?.response?.data;

  if (data?.error) {
    const key = `apiError.${data.error}`;
    const translated = t(key, { defaultValue: '' });
    // i18next returns the key itself if missing — check both
    if (translated && translated !== key && translated !== '') {
      return translated;
    }
  }

  // Fall back to raw backend message, then to the provided fallback
  return data?.message || fallback || t('common.unknownError');
}
